import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Lightweight post-OTP-signup email-collection modal.
 *
 * Rendered on the homepage. Shows automatically when the URL has
 * `?prompt_email=1` (set by the OTP verify success handler in Login.js) AND
 * the signed-in user does not yet have an email on file. We deliberately use
 * a homepage-mounted modal instead of an in-page mode switch on /login,
 * because iPhone Chrome / iOS WebKit had a race condition between state-
 * driven mode switches, autoFocus on the new input, and the soft-keyboard
 * dismiss animation that caused a blank screen after OTP verification.
 *
 * This modal can be dismissed — the email is optional. The user can add it
 * later from My Account.
 */
const EmailPromptModal = () => {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Only show once we have a confirmed phone-only signed-in user with no email
    if (searchParams.get('prompt_email') === '1' && user && !user.email) {
      setOpen(true);
    }
  }, [searchParams, user]);

  const close = () => {
    setOpen(false);
    // Strip the query param so the modal doesn't reopen on refresh
    const params = new URLSearchParams(searchParams);
    params.delete('prompt_email');
    setSearchParams(params, { replace: true });
  };

  const submit = async () => {
    if (email.trim() && !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!email.trim()) { close(); return; }
    setSaving(true);
    try {
      await authAPI.updateProfile({ email: email.trim() });
      await checkAuth();
      toast.success('Email saved! Verify it from My Account.');
    } catch (e) {
      // Non-blocking — email is optional
      console.warn('Email save failed', e);
    } finally {
      setSaving(false);
      close();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="email-prompt-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-bold text-[#0A0B10]" style={{ fontFamily: 'Google Sans Flex' }}>
            Welcome to GenzoX
          </h2>
          <button onClick={close} aria-label="Close" data-testid="email-prompt-close"
            className="text-[#9CA3AF] hover:text-[#0A0B10] p-1 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">Add your email (optional) to receive ad updates and important notifications.</p>
        <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5">
          <Mail size={13} /> Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid="email-prompt-input"
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
        />
        <button
          onClick={submit}
          disabled={saving}
          data-testid="email-prompt-submit"
          className="mt-4 w-full bg-[#1800ad] text-white rounded-2xl py-3 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-50"
        >
          {saving ? 'Saving…' : email.trim() ? 'Save & Continue' : 'Skip & Continue'}
        </button>
      </div>
    </div>
  );
};

export default EmailPromptModal;
